import React, { useEffect, useMemo, useState } from "react";
import { Container, InfoModal, InfoModalHeader, InfoModalBody } from "../../../components";
import { Badge, Box, Button, Drawer, ExpandableCard, Heading, Icon, RadioGroup, Tabs, Text } from "@telia/teddy";
import _find from "lodash/find";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import _isNumber from "lodash/isNumber";
import _size from "lodash/size";
import { connect, useDispatch, useSelector } from "react-redux";
import { FWA_LABELS, GA_CATEGORIES } from "../../../../../constants";
import PAGES from "../../../../../constants/pages";
import { Modal, RDContentBlock, RootState } from "../../../../../models";
import { DiyPackageTypes, PackageType, diyPackageTypes, packageTypes } from "../../../../../models/common";
import { Package } from "../../../../../models/state";
import { isRunningOnClientSide } from "../../../../../shared/util";
import { usePrevious } from "../../../checkout/hooks";
import SubscriptionTextBlock from "../../../components/components/molecules/SubscriptionTextBlock/SubscriptionTextBlock";
import {
    getFwaCategory,
    trackFirstLoad,
    trackProductClick,
    trackAddToCart,
    trackFunnelEvent,
} from "../../../google-analytics";
import * as LOADING_SELECTORS from "../../../loading/state/selectors";
import { RenderCfContent } from "../../../modals/utils/util";
import TeliaPlayBenefits from "../../components/molecules/TeliaPlayBenefits/TeliaPlayBenefits";
import { lookupPackages, addBroadbandPackageToCart } from "../../state/actions";
import * as AT from "../../state/actionTypes";
import * as FWA_SELECTORS from "../../state/selectors";
import TEXT from "../../text-consts";
import { SMART_WIFI_ROUTER_PRODUCT_IDS } from "../../constants";
import WirelessInfoDrawer from "./WirelessInfoDrawer";
import "./ChooseSubscription.less";

const TABS = [
    { id: "FWA", name: "Bredbånd" },
    { id: "FWA_TV", name: "Bredbånd og TV" },
];

const getDIYPackage = (selectedPackage?: Package, selectedPackageLinked?: Package) => {
    if (selectedPackage && diyPackageTypes.includes(selectedPackage.type as DiyPackageTypes)) {
        return selectedPackage;
    } else if (selectedPackageLinked && diyPackageTypes.includes(selectedPackageLinked.type as DiyPackageTypes)) {
        return selectedPackageLinked;
    } else {
        return null;
    }
};

const getTechnicianPackage = (selectedPackage?: Package, selectedPackageLinked?: Package) => {
    if (selectedPackage && [...packageTypes, "4g"].includes(selectedPackage.type as PackageType)) {
        return selectedPackage;
    } else if (selectedPackageLinked && [...packageTypes, "4g"].includes(selectedPackageLinked.type)) {
        return selectedPackageLinked;
    } else {
        return null;
    }
};

const getInstallmentPrice = (
    selectedPackage: Package | null,
    id: number,
): { price: number; striketrough?: number } | null => {
    if (!selectedPackage) {
        return null;
    }

    const product = _find(selectedPackage.products, (p) => p.id === id);
    if (!product) {
        return null;
    }

    return _isNumber(product.installationPrice.discountPrice) &&
        product.installationPrice.discountPrice < product.installationPrice.regularPrice
        ? {
              striketrough: product.installationPrice.regularPrice,
              price: product.installationPrice.discountPrice,
          }
        : { price: product.installationPrice.regularPrice };
};

const isCampaign = (selectedPackage: Package | null, id: number): boolean => {
    if (!selectedPackage) {
        return false;
    }

    const product = _find(selectedPackage.products, (p) => p.id === id);
    if (!product) {
        return false;
    }

    return (
        _isNumber(product.installationPrice.discountPrice) &&
        product.installationPrice.discountPrice < product.installationPrice.regularPrice
    );
};

interface ChooseSubscriptionProps {
    address: string;
    backUrl?: string;
    contentBlocks: RDContentBlock[];
    defaultTab?: "FWA" | "FWA_TV";
    diyModal: Modal | undefined;
    isFetchingPackages: boolean;
    isLoading: boolean;
    isNoPackages: boolean;
    packages: Package[];
    successfullyAddedProductToCart: boolean;
    tvPackages: Package[];
}

const ChooseSubscription: React.FC<ChooseSubscriptionProps> = ({
    backUrl,
    packages,
    tvPackages,
    isNoPackages,
    isLoading,
    isFetchingPackages,
    successfullyAddedProductToCart,
    address,
    contentBlocks,
    defaultTab,
    diyModal,
}) => {
    const dispatch = useDispatch();
    const [selectedSubscription, setSelectedSubscription] = useState<number | null>(null);
    const selectedPackage = useSelector((state: RootState) =>
        FWA_SELECTORS.getPackageFromId(state, selectedSubscription ?? undefined),
    );
    const selectedPackageLinked = useSelector((state: RootState) =>
        FWA_SELECTORS.getPackageFromId(state, _get(selectedPackage, "linkedPackageId")),
    );
    const diyPackage = getDIYPackage(selectedPackage, selectedPackageLinked);
    const technicianPackage = getTechnicianPackage(selectedPackage, selectedPackageLinked);
    const technicianInstallmentPrice = getInstallmentPrice(technicianPackage, 1933); // 1933 is "Installasjonskostnad Trådløst Bredbånd 5G"
    const displayFlashText = isCampaign(technicianPackage, 1933); // 1933 is "Installasjonskostnad Trådløst Bredbånd 5G"
    const prevSuccessfullyAddedProductToCart = usePrevious(successfullyAddedProductToCart);
    useEffect(() => {
        if (successfullyAddedProductToCart && successfullyAddedProductToCart !== prevSuccessfullyAddedProductToCart) {
            window.location.assign(PAGES.FWA.CUSTOMER.URL);
        }
    }, [successfullyAddedProductToCart, prevSuccessfullyAddedProductToCart]);

    useEffect(() => {
        // oxlint-disable-next-line typescript/ban-ts-comment
        // @ts-expect-error
        dispatch(lookupPackages()).then((data) => {
            const availablePackages: Package[] = _get(data, "action.payload.availablePackages");
            if (!availablePackages || _size(availablePackages) === 0) {
                trackFunnelEvent({
                    step: "1",
                    label: FWA_LABELS[0],
                    name: "FWA - Trådløst Bredbånd",
                    isUIEvent: false,
                    feedback: "Ingen treff, men du kan fortsatt få internett",
                });
                if (backUrl) {
                    window.location.assign(backUrl);
                }
            }
        });
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, [lookupPackages, backUrl]);

    const [isClientSide, setIsClientSide] = useState(false);
    useEffect(() => {
        if (isRunningOnClientSide()) {
            setIsClientSide(true);
        }
    }, []);

    // Trigger GA funnel
    useEffect(() => {
        trackFunnelEvent({ step: "1", label: FWA_LABELS[0], name: "FWA - Trådløst Bredbånd" });
    }, []);

    const [selectedTabsId, setSelectedTabsId] = useState<string>();
    const [modal, setModal] = useState<Modal | null>(null);
    const isSubmitting = useSelector((state: RootState) =>
        LOADING_SELECTORS.isTypeLoading(state, AT.FWA_ADD_PACKAGE_TO_CART),
    );
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [wirelessInfoDrawerOpen, setWirelessInfoDrawerOpen] = useState(false);
    const [drawerShowDiyModal, setDrawerShowDiyModal] = useState(false);
    const [installOption, setInstallOption] = useState<"diy" | "technician">("diy");
    const showTabs = _size(packages) > 0 && _size(tvPackages) > 0;
    const packagesToShow = useMemo(() => {
        if (selectedTabsId === "FWA") {
            return packages;
        } else if (selectedTabsId === "FWA_TV") {
            return tvPackages;
        } else {
            return [];
        }
    }, [selectedTabsId, packages, tvPackages]);

    const prevPackagesToShow = usePrevious(packagesToShow);
    useEffect(() => {
        if (_size(packagesToShow) > 0 && packagesToShow !== prevPackagesToShow) {
            trackFirstLoad(
                packagesToShow,
                selectedTabsId === "FWA_TV" ? GA_CATEGORIES.SUBSCRIPTION_FWA_TV : GA_CATEGORIES.SUBSCRIPTION_FWA,
            );
        }
    }, [packagesToShow, prevPackagesToShow, selectedTabsId]);

    const backUrlAddressSuffix = useMemo(() => {
        const backUrlQuery = (backUrl || "").split("?")[1];
        const address = new URLSearchParams(backUrlQuery || "").get("address");
        return address ? `?platformType=HFC&address=${address}` : "";
    }, [backUrl]);

    useEffect(() => {
        if (defaultTab) {
            setSelectedTabsId(defaultTab);
        }
    }, [defaultTab]);

    const handleChangeTab = (id: string): void => {
        setSelectedTabsId(id);

        // Track UI event
        const uiEventName = id === "FWA_TV" ? "Bredbånd og TV tab" : "Bredbånd tab";
        trackFunnelEvent({
            step: "1",
            label: "Velg hastighet",
            name: "FWA - Trådløst Bredbånd",
            isUIEvent: true,
            uiEventName,
        });
    };

    const handleSelectSubscription = (item: Package, index: number) => {
        if (item.id === selectedSubscription) {
            setSelectedSubscription(null);
            return;
        }

        setSelectedSubscription(item.id);

        // Track UI event
        trackFunnelEvent({
            step: "1",
            label: "Velg hastighet",
            name: "FWA - Trådløst Bredbånd",
            isUIEvent: true,
            uiEventName: item.shortName,
        });

        // Track product click
        trackProductClick(
            item,
            index,
            getFwaCategory(
                item.type,
                selectedTabsId === "FWA_TV" ? GA_CATEGORIES.SUBSCRIPTION_FWA_TV : GA_CATEGORIES.SUBSCRIPTION_FWA,
            ),
        );
    };

    const handleOpenModal = (textBlock: any) => {
        setModal(textBlock);

        // Track UI event
        const uiEventName = `${textBlock.linkText} - ${textBlock.heading}`;
        trackFunnelEvent({
            step: "1",
            label: "Velg hastighet",
            name: "FWA - Trådløst Bredbånd",
            isUIEvent: true,
            uiEventName,
        });
    };

    const handleProductAdd = async (item: Package, isDIY: boolean) => {
        const additionalProducts: number[] = [];
        const product = {
            id: item.id,
            // additionalProducts: item.additionalProducts,
            // TODO: Need design / new step to choose additional products
            additionalProducts,
        };

        try {
            const uiEventName = isDIY ? "Jeg gjør det selv" : "Jeg vil bestille montør";
            await trackFunnelEvent({
                step: "1",
                label: FWA_LABELS[0],
                name: "FWA - Trådløst Bredbånd",
                isUIEvent: true,
                uiEventName,
            });
            await trackAddToCart(
                [item],
                getFwaCategory(
                    item.type,
                    selectedTabsId === "FWA_TV" ? GA_CATEGORIES.SUBSCRIPTION_FWA_TV : GA_CATEGORIES.SUBSCRIPTION_FWA,
                ),
            );
        } finally {
            dispatch(addBroadbandPackageToCart(product));
        }
    };

    const handleOpenDrawer = () => {
        setDrawerOpen(true);
        setDrawerShowDiyModal(false);
        setInstallOption(diyPackage ? "diy" : "technician");
        const uiEventName = diyPackage ? "Legg til - Jeg gjør det selv" : "Legg til - Jeg vil bestille montør";
        trackFunnelEvent({
            step: "1",
            label: "Velg montering",
            name: "FWA - Trådløst Bredbånd",
            isUIEvent: true,
            uiEventName,
        });
    };

    const handleOpenWirelessInfoDrawer = () => {
        setWirelessInfoDrawerOpen(true);
    };

    const cameFromWired = isClientSide && document.referrer.includes("/internett/bredband");

    return (
        <div className="fwa-choose-subscription">
            {isFetchingPackages && (
                <Container size="small">
                    <Heading mb="100" as="h2" variant="title-200" data-di-mask>
                        {address}
                    </Heading>
                    <Text mb="100">Vi sjekker din tilgjengelighet, dette kan ta opptil ett minutt</Text>
                </Container>
            )}
            {packagesToShow && !isFetchingPackages && (
                <Container className="fwa-choose-subscription__header" size="small" noMargin>
                    <Heading mb="100" variant="title-300" as="h1" className="fwa-choose-subscription__header-title">
                        Dette kan vi levere hos deg
                    </Heading>
                    {cameFromWired && (
                        <div className="fwa-choose-subscription__description">
                            <Icon
                                className="fwa-choose-subscription__description-icon"
                                name="wireless"
                                aria-hidden="true"
                                focusable="false"
                            />
                            <Text mb="100" className="fwa-choose-subscription__header-sub-title">
                                Fast bredbånd er ikke tilgjengelig hos deg, men vi kan levere Trådløst Bredbånd via 5G i
                                stedet – stabilt og raskt!{" "}
                                <Button p="10" variant="text" onClick={handleOpenWirelessInfoDrawer}>
                                    Les mer
                                </Button>
                            </Text>
                        </div>
                    )}
                    {showTabs && !cameFromWired && (
                        <Text mb="100" className="fwa-choose-subscription__header-sub-title">
                            Velg mellom Trådløst Bredbånd og TV, levert via mobilnettet.
                        </Text>
                    )}
                </Container>
            )}
            {showTabs && (
                <Container className="fwa-choose-subscription__tabs">
                    <Tabs value={selectedTabsId} onValueChange={handleChangeTab}>
                        <Tabs.List>
                            {TABS.map((tab) => (
                                <Tabs.Trigger key={tab.id} value={tab.id}>
                                    {tab.name}
                                </Tabs.Trigger>
                            ))}
                        </Tabs.List>
                    </Tabs>
                </Container>
            )}
            <Container size="small" noMargin>
                {!isLoading && isNoPackages && (
                    <div>
                        <div>
                            <Heading mb="100" as="h1" variant="title-200">
                                {TEXT.PACKAGES.PACKAGES_NOT_FOUND}
                            </Heading>
                        </div>
                        <div>
                            {TEXT.PACKAGES.NOT_FOUND_OPTIONS_HEADING}
                            <ul>
                                <li dangerouslySetInnerHTML={{ __html: TEXT.PACKAGES.INTERNET_HOME }} />
                                <li dangerouslySetInnerHTML={{ __html: TEXT.PACKAGES.INTERNET_CABIN }} />
                            </ul>
                        </div>
                    </div>
                )}
                {packagesToShow && (
                    <ExpandableCard
                        type="single"
                        collapsible
                        value={selectedSubscription ? `${selectedSubscription}` : ""}
                        onValueChange={(value) => {
                            const index = packagesToShow.findIndex((pkg) => `${pkg.id}` === value);
                            const item = packagesToShow[index];
                            if (item) {
                                return handleSelectSubscription(item, index);
                            }
                            setSelectedSubscription(null);
                        }}
                    >
                        {packagesToShow.map((item) => {
                            const showStrikethrough =
                                item.monthlyPrice.discountPrice !== item.monthlyPrice.regularPrice;
                            const discountedPrice = item.monthlyPrice.regularPrice - item.monthlyPrice.discountPrice;
                            return (
                                <ExpandableCard.Item key={item.id} value={`${item.id}`}>
                                    <ExpandableCard.Trigger className="fwa-choose-subscription__card-trigger">
                                        {item.flashText || (discountedPrice && discountedPrice > 0) ? (
                                            <ExpandableCard.Slot
                                                align="top-right"
                                                className="fwa-choose-subscription__badge"
                                            >
                                                {item.flashText && (
                                                    <Badge
                                                        variant="special"
                                                        style={{
                                                            backgroundColor: item.flashText.color,
                                                            color: item.flashText.textColor || "#fff",
                                                        }}
                                                        hideIcon
                                                    >
                                                        {item.flashText.text}
                                                    </Badge>
                                                )}
                                                {discountedPrice && discountedPrice > 0 && (
                                                    <Badge
                                                        variant="special"
                                                        hideIcon
                                                        className="fwa-choose-subscription__badge fwa-choose-subscription__badge--discount"
                                                    >
                                                        {discountedPrice},-/md i rabatt
                                                    </Badge>
                                                )}
                                            </ExpandableCard.Slot>
                                        ) : null}
                                        <Text as="p" mb="25" variant="paragraph-100">
                                            {item.name || ""}
                                        </Text>
                                        <ExpandableCard.Header>
                                            <ExpandableCard.Description>
                                                <Heading mb="100" as="h4" variant="title-200">
                                                    {item.shortName || ""}
                                                </Heading>
                                                <ExpandableCard.Price>
                                                    {showStrikethrough && (
                                                        <Text as="p" mr="100" variant="paragraph-100">
                                                            <s>{item.monthlyPrice.regularPrice},-</s>
                                                        </Text>
                                                    )}
                                                    <Heading mb="100" as="h4" variant="title-200">
                                                        {item.monthlyPrice.discountPrice},-
                                                    </Heading>
                                                    <Text as="p" variant="paragraph-100">
                                                        /md.
                                                    </Text>
                                                </ExpandableCard.Price>
                                            </ExpandableCard.Description>
                                            <ExpandableCard.Indicator />
                                        </ExpandableCard.Header>
                                        {showStrikethrough && (
                                            <>
                                                <ExpandableCard.Line />
                                                <ExpandableCard.HiddenContainer>
                                                    <Text as="p" variant="additional-100">
                                                        {TEXT.PACKAGES.FIRST_YEAR_DISCOUNT_INFO(discountedPrice)}
                                                    </Text>
                                                </ExpandableCard.HiddenContainer>
                                            </>
                                        )}
                                    </ExpandableCard.Trigger>
                                    <ExpandableCard.Content>
                                        {showStrikethrough && (
                                            <Text as="p" mb="200" variant="additional-100">
                                                {TEXT.PACKAGES.FIRST_YEAR_DISCOUNT_INFO(discountedPrice)} <br />
                                                12 md. bindingstid
                                            </Text>
                                        )}
                                        {(item.textBlocks || []).map((textBlock) => {
                                            switch (textBlock.__type) {
                                                case "TextBlock":
                                                    // Replace placeholders from CF with actual values
                                                    const smartWifiRouterPrice = item.products.find((product) =>
                                                        SMART_WIFI_ROUTER_PRODUCT_IDS.includes(product.id),
                                                    )?.monthlyPrice.regularPrice;

                                                    const uspList = textBlock.uspList?.map((usp) => {
                                                        if (usp.id === "Smart Wifi Router") {
                                                            return {
                                                                ...usp,
                                                                title: usp.title?.replace(
                                                                    "[$$$]",
                                                                    smartWifiRouterPrice !== undefined
                                                                        ? String(smartWifiRouterPrice)
                                                                        : "",
                                                                ),
                                                            };
                                                        }
                                                        return usp;
                                                    });
                                                    return (
                                                        <SubscriptionTextBlock
                                                            headingSize="xs"
                                                            headingTag="h3"
                                                            key={textBlock.heading}
                                                            textBlock={{ ...textBlock, uspList }}
                                                        />
                                                    );
                                                case "Modal":
                                                    if (!textBlock.linkText) {
                                                        return null;
                                                    }
                                                    return (
                                                        <div
                                                            key={textBlock.linkText}
                                                            className="fwa-choose-subscription__modal-link"
                                                        >
                                                            <Button
                                                                variant="text"
                                                                onClick={() => handleOpenModal(textBlock)}
                                                            >
                                                                {textBlock.linkText}
                                                            </Button>
                                                        </div>
                                                    );
                                            }
                                        })}
                                        {selectedTabsId === "FWA_TV" && (
                                            <TeliaPlayBenefits
                                                {...(item.textBlocks.find(
                                                    (textBlock) =>
                                                        textBlock.__type === "Modal" &&
                                                        textBlock.forEntryId === "TELIA_PLAY",
                                                ) as Modal)}
                                            />
                                        )}
                                        <div className="fwa-choose-subscription__button-container">
                                            <ExpandableCard.Button
                                                variant="primary"
                                                onClick={handleOpenDrawer}
                                                className="fwa-choose-subscription__button-add"
                                            >
                                                <Icon name="cart" aria-hidden="true" focusable="false" />
                                                Legg til
                                            </ExpandableCard.Button>
                                        </div>
                                    </ExpandableCard.Content>
                                </ExpandableCard.Item>
                            );
                        })}
                    </ExpandableCard>
                )}
            </Container>
            {_size(packagesToShow) > 1 && (
                <Container className="fwa-choose-subscription__grey-container" noPadding size="large">
                    {contentBlocks.map((block) => (
                        <Container key={`fwa-content-block-${block.title}`} size="small">
                            <Heading mb="100" as="h2" variant="title-200">
                                {block.title}
                            </Heading>
                            <div dangerouslySetInnerHTML={{ __html: block.text }} />
                            {block.buttonLink && block.buttonText && (
                                <Button
                                    className="fwa-choose-subscription__content-block-button"
                                    variant="secondary"
                                    onClick={(): void =>
                                        window.location.assign(`${block.buttonLink}${backUrlAddressSuffix}`)
                                    }
                                >
                                    {block.buttonText}
                                </Button>
                            )}
                        </Container>
                    ))}
                </Container>
            )}
            {isClientSide && (
                <InfoModal
                    className="fwa-choose-subscription__info-modal"
                    open={!!modal}
                    setOpen={() => setModal(null)}
                    size="large"
                    ariaLabelledBy=""
                >
                    {modal && (
                        <>
                            <InfoModalHeader headerText={modal.heading} ingress={modal.ingress} />
                            <InfoModalBody>
                                <RenderCfContent modal={modal} />
                            </InfoModalBody>
                        </>
                    )}
                </InfoModal>
            )}
            {isClientSide && (
                <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                    <Drawer.Overlay>
                        <Drawer.Content size="md">
                            <Drawer.Close slot="floating" />
                            {drawerShowDiyModal ? (
                                <>
                                    <Drawer.Title className="fwa-choose-subscription__drawer-title" variant="title-300">
                                        {diyModal?.heading}
                                    </Drawer.Title>
                                    {diyModal?.ingress && (
                                        <Text as="p" variant="paragraph-100">
                                            {diyModal.ingress}
                                        </Text>
                                    )}
                                    {diyModal && <RenderCfContent modal={diyModal} />}
                                </>
                            ) : (
                                <>
                                    <Drawer.Title className="fwa-choose-subscription__drawer-title" variant="title-300">
                                        5G-antenne inkludert
                                    </Drawer.Title>
                                    <Text as="p" variant="paragraph-100">
                                        En liten 5G-antenne følger med abonnementet (verdi{" "}
                                        <span className="strikethrough">1 999,-</span>)
                                    </Text>

                                    <Box mb="400">
                                        <div>
                                            <ul className="equipment-list">
                                                <li className="equipment-list__item">
                                                    <Icon
                                                        name="check-circle"
                                                        size="md"
                                                        aria-hidden="true"
                                                        focusable="false"
                                                    />
                                                    Ingen verktøy nødvendig
                                                </li>
                                                <li className="equipment-list__item">
                                                    <Icon
                                                        name="check-circle"
                                                        size="md"
                                                        aria-hidden="true"
                                                        focusable="false"
                                                    />
                                                    Vi guider deg steg for steg
                                                </li>
                                                <li className="equipment-list__item">
                                                    <Icon
                                                        name="check-circle"
                                                        size="md"
                                                        aria-hidden="true"
                                                        focusable="false"
                                                    />
                                                    Du slipper å vente på montør
                                                </li>
                                            </ul>
                                        </div>
                                        <div>
                                            {diyModal && (
                                                <Button
                                                    className="fwa-choose-subscription__drawer-link"
                                                    onClick={() => setDrawerShowDiyModal(true)}
                                                    variant="text-underline"
                                                >
                                                    {diyModal.linkText}
                                                    <Icon
                                                        name="arrow-right"
                                                        size="xs"
                                                        aria-hidden="true"
                                                        focusable="false"
                                                    />
                                                </Button>
                                            )}
                                        </div>
                                    </Box>
                                    <div className="fwa-choose-subscription__drawer-options">
                                        <Heading
                                            as="h3"
                                            variant="title-200"
                                            mb="100"
                                            className="fwa-choose-subscription__drawer-options-heading"
                                        >
                                            Velg hvordan du vil sette opp antennen
                                        </Heading>
                                        <RadioGroup
                                            value={installOption}
                                            onValueChange={(v) => setInstallOption(v as "diy" | "technician")}
                                        >
                                            {!_isEmpty(diyPackage) && (
                                                <RadioGroup.Item>
                                                    <RadioGroup.Trigger value="diy">
                                                        <RadioGroup.Indicator />
                                                    </RadioGroup.Trigger>
                                                    <RadioGroup.Label>Gjør det selv</RadioGroup.Label>
                                                </RadioGroup.Item>
                                            )}
                                            {!_isEmpty(technicianPackage) && (
                                                <RadioGroup.Item>
                                                    <RadioGroup.Trigger value="technician">
                                                        <RadioGroup.Indicator />
                                                    </RadioGroup.Trigger>
                                                    <RadioGroup.Label>
                                                        Få hjelp av montør
                                                        {technicianInstallmentPrice &&
                                                            ` (+${Math.ceil(technicianInstallmentPrice.price)},-)`}
                                                    </RadioGroup.Label>
                                                    {displayFlashText && (
                                                        <Badge variant="special">
                                                            Kampanje - halv pris på installasjon
                                                        </Badge>
                                                    )}
                                                </RadioGroup.Item>
                                            )}
                                        </RadioGroup>
                                    </div>
                                </>
                            )}
                            <Drawer.Footer className="fwa-choose-subscription__drawer-footer">
                                {drawerShowDiyModal ? (
                                    <Button
                                        className="fwa-choose-subscription__drawer-button"
                                        onClick={() => setDrawerShowDiyModal(false)}
                                    >
                                        <Icon name="arrow-left" size="xs" aria-hidden="true" focusable="false" />
                                        Tilbake
                                    </Button>
                                ) : (
                                    <Button
                                        className="fwa-choose-subscription__drawer-button"
                                        loading={isSubmitting}
                                        onClick={() => {
                                            if (installOption === "diy" && diyPackage) {
                                                handleProductAdd(diyPackage, true);
                                            } else if (installOption === "technician" && technicianPackage) {
                                                handleProductAdd(technicianPackage, false);
                                            }
                                        }}
                                    >
                                        Fortsett
                                        <Icon name="arrow-right" size="xs" aria-hidden="true" focusable="false" />
                                    </Button>
                                )}
                            </Drawer.Footer>
                        </Drawer.Content>
                    </Drawer.Overlay>
                </Drawer>
            )}
            <WirelessInfoDrawer open={wirelessInfoDrawerOpen} onOpenChange={setWirelessInfoDrawerOpen} />
        </div>
    );
};

const mapStateToProps = (state: RootState) => ({
    backUrl: FWA_SELECTORS.getBackUrl(state),
    packages: FWA_SELECTORS.getPackagesToShow(state),
    tvPackages: FWA_SELECTORS.getTVPackagesToShow(state),
    isNoPackages: FWA_SELECTORS.isNoPackages(state),
    isLoading: LOADING_SELECTORS.isLoading(state),
    isFetchingPackages: LOADING_SELECTORS.isTypeLoading(state, AT.FWA_LOOKUP_PACKAGES),
    successfullyAddedProductToCart: FWA_SELECTORS.successfullyAddedProductToCart(state),
    address: FWA_SELECTORS.getSelectedAddress(state, true),
    contentBlocks: FWA_SELECTORS.getFwaContentBlocks(state),
    defaultTab: FWA_SELECTORS.getDefaultTab(state),
    diyModal: FWA_SELECTORS.getDIYModal(state) ?? undefined,
});

export default connect(mapStateToProps)(ChooseSubscription);
